import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

export interface BuyerNotification {
  id: string;
  buyer_id: string;
  type: string;
  content: any;
  read_status: boolean;
  created_at: string;
}

export const buyerNotificationsService = {
  // Get all notifications for a buyer
  async getBuyerNotifications(buyerId: string): Promise<BuyerNotification[]> {
    try {
      const { data, error } = await supabase
        .from('buyer_notifications')
        .select('*')
        .eq('buyer_id', buyerId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('Error fetching buyer notifications', { error: error.message });
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error('Error fetching buyer notifications', { error });
      return [];
    }
  },

  // Get unread notification count for a buyer
  async getUnreadCount(buyerId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('buyer_notifications')
        .select('*', { count: 'exact', head: true })
        .eq('buyer_id', buyerId)
        .eq('read_status', false);

      if (error) {
        logger.error('Error fetching unread count', { error: error.message });
        return 0;
      }

      return count || 0;
    } catch (error) {
      logger.error('Error fetching unread count', { error });
      return 0;
    }
  },

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('buyer_notifications')
        .update({ read_status: true })
        .eq('id', notificationId);

      if (error) {
        logger.error('Error marking notification as read', { error: error.message });
      }
    } catch (error) {
      logger.error('Error marking notification as read', { error });
    }
  },

  // Mark all notifications as read for a buyer
  async markAllAsRead(buyerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('buyer_notifications')
        .update({ read_status: true })
        .eq('buyer_id', buyerId)
        .eq('read_status', false);

      if (error) {
        logger.error('Error marking all notifications as read', { error: error.message });
      }
    } catch (error) {
      logger.error('Error marking all notifications as read', { error });
    }
  },

  // Subscribe to real-time notifications
  subscribeToNotifications(buyerId: string, callback: (notification: any) => void) {
    const channel = supabase
      .channel('buyer-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'buyer_notifications',
          filter: `buyer_id=eq.${buyerId}`
        },
        callback
      )
      .subscribe();

    return channel;
  }
};